import type { MessageThinking, StreamingToolEvent } from "@/types/chat.type";

export type StreamingThinkingBlock = MessageThinking & {
  id: string;
  isLive: boolean;
};

export type StreamingTextBlock = {
  id: string;
  content: string;
  isLive: boolean;
};

export type StreamingBlock =
  | { kind: "thinking"; block: StreamingThinkingBlock }
  | { kind: "tool"; event: StreamingToolEvent }
  | { kind: "text"; block: StreamingTextBlock };

/** Builds an ordered timeline of text, thinking, and tool calls for a live turn. */
export function createStreamingTurnBuilder() {
  let blocks: StreamingBlock[] = [];
  let fullText = "";
  let thinkingText = "";
  let phaseStartedAt: number | null = null;
  let phaseDurationMs = 0;

  const currentPhaseDurationMs = () =>
    phaseDurationMs + (phaseStartedAt !== null ? Date.now() - phaseStartedAt : 0);

  const endPhaseTimer = () => {
    if (phaseStartedAt === null) return;
    phaseDurationMs += Date.now() - phaseStartedAt;
    phaseStartedAt = null;
  };

  const finalizeLiveText = () => {
    const last = blocks[blocks.length - 1];
    if (last?.kind === "text" && last.block.isLive) {
      last.block.isLive = false;
    }
  };

  const finalizeLiveThinking = () => {
    endPhaseTimer();
    if (!thinkingText.trim()) return;

    const last = blocks[blocks.length - 1];
    if (last?.kind === "thinking" && last.block.isLive) {
      last.block.text = thinkingText;
      last.block.durationMs = phaseDurationMs;
      last.block.isLive = false;
    } else {
      blocks.push({
        kind: "thinking",
        block: {
          id: `think-${blocks.length}`,
          text: thinkingText,
          durationMs: phaseDurationMs,
          isLive: false,
        },
      });
    }

    thinkingText = "";
    phaseDurationMs = 0;
  };

  const syncLiveThinking = (): StreamingBlock[] => {
    const last = blocks[blocks.length - 1];
    const durationMs = currentPhaseDurationMs();

    if (last?.kind === "thinking" && last.block.isLive) {
      last.block.text = thinkingText;
      last.block.durationMs = durationMs;
      return [...blocks];
    }

    blocks.push({
      kind: "thinking",
      block: {
        id: `think-${blocks.length}`,
        text: thinkingText,
        durationMs: durationMs,
        isLive: true,
      },
    });
    return [...blocks];
  };

  const appendText = (content: string): StreamingBlock[] => {
    fullText += content;
    const last = blocks[blocks.length - 1];
    if (last?.kind === "text" && last.block.isLive) {
      last.block.content += content;
      return [...blocks];
    }

    blocks.push({
      kind: "text",
      block: {
        id: `text-${blocks.length}`,
        content,
        isLive: true,
      },
    });
    return [...blocks];
  };

  return {
    onTextChunk(content: string): StreamingBlock[] {
      finalizeLiveThinking();
      return appendText(content);
    },

    onStreamComplete(text: string): StreamingBlock[] {
      finalizeLiveThinking();
      finalizeLiveText();
      fullText = text;
      return [...blocks];
    },

    onThinkingChunk(content: string): StreamingBlock[] {
      finalizeLiveText();
      if (phaseStartedAt === null) phaseStartedAt = Date.now();
      thinkingText += content;
      return syncLiveThinking();
    },

    onToolStart(event: StreamingToolEvent): StreamingBlock[] {
      finalizeLiveThinking();
      finalizeLiveText();
      blocks.push({ kind: "tool", event });
      return [...blocks];
    },

    onToolEnd(
      toolCallId: string,
      patch: Pick<StreamingToolEvent, "result" | "status" | "endedAt">,
    ): StreamingBlock[] {
      const block = blocks.find(
        (b) => b.kind === "tool" && b.event.toolCallId === toolCallId,
      );
      if (block?.kind === "tool") {
        block.event = { ...block.event, ...patch };
      }
      return [...blocks];
    },

    /** Thinking time so far: finished phases plus the one in progress. */
    thinkingMsSoFar(): number {
      const done = blocks.reduce(
        (sum, b) => (b.kind === "thinking" && !b.block.isLive ? sum + b.block.durationMs : sum),
        0,
      );
      return done + currentPhaseDurationMs();
    },

    finalize(): {
      blocks: StreamingBlock[];
      fullText: string;
      totalThinkingText: string;
      totalThinkingMs: number;
    } {
      finalizeLiveThinking();
      finalizeLiveText();
      const thinkingBlocks = blocks
        .filter((b): b is Extract<StreamingBlock, { kind: "thinking" }> => b.kind === "thinking")
        .map((b) => b.block);
      return {
        blocks: [...blocks],
        fullText,
        totalThinkingText: thinkingBlocks.map((b) => b.text).join("\n\n"),
        totalThinkingMs: thinkingBlocks.reduce((sum, b) => sum + b.durationMs, 0),
      };
    },

    reset() {
      blocks = [];
      fullText = "";
      thinkingText = "";
      phaseStartedAt = null;
      phaseDurationMs = 0;
    },
  };
}
